// ============================================================================
// StakeTek hardware launch config — the SINGLE editable source for the
// Phase 2–3 products (Installation Kits, Flat Bracket, timber supports).
// Edit prices and hardware specs here, nowhere else. The product pages,
// add-ons, JSON-LD and totals all read from this file.
//
// Installation-kit prices CONFIRMED 2026-07-12. When you change a price here,
// change the matching Shopify variant price too (Shopify is the checkout, so
// the two must stay in sync).
// ============================================================================

// ----------------------------------------------------------------------------
// DISPATCH WINDOW — SINGLE SOURCE OF TRUTH.
// A delivery-time representation shown to customers on the product pages and
// published to Google in Product schema handlingTime, so it must match what
// actually happens in the workshop. Update it here and every place that quotes
// a dispatch time follows. Applies to the whole bracket range: the flat bracket
// now dispatches on the same window as the supported bracket.
// ----------------------------------------------------------------------------
export const DISPATCH_WINDOW = "3–5 business days";

// ----------------------------------------------------------------------------
// Timber support options for the slide-on bracket. The choice is carried to
// the Shopify order as a cart line attribute (same mechanism as Finish) —
// both supports sell at the same public price.
// ----------------------------------------------------------------------------
export const TIMBER_SUPPORTS = [
  {
    id: "standard",
    name: "Standard 45 × 90 Timber Support",
    shortName: "Standard 45 × 90",
    desc: "Designed around 45 × 90 mm F5 timber. This is the main stocked option.",
    stocked: true,
    dispatchNote: `Main stocked option. Usually dispatched within ${DISPATCH_WINDOW}.`,
    kitType: "standard",
  },
  {
    id: "plinth",
    name: "25 × 150 Plinth Timber Support",
    shortName: "25 × 150 Plinth",
    desc: "Designed for 25 × 150 mm plinth timber and wider timber-support applications.",
    stocked: false,
    dispatchNote: "Selected sizes may require additional manufacturing time.",
    kitType: "plinth",
  },
];
export const DEFAULT_TIMBER_SUPPORT = TIMBER_SUPPORTS[0];

// ----------------------------------------------------------------------------
// Installation-kit types. Contents are per bracket. No washers in any kit.
// Kits are optional and sold separately.
//
// CONFIRMED 2026-07-12:
//  - The flat bracket uses the SAME kit as the standard supported bracket, so
//    the flat bracket page matches kits with kitTypeId "standard".
//  - The plinth timber support uses 20 mm outdoor screws instead of 40 mm.
// ----------------------------------------------------------------------------
export const KIT_TYPES = {
  standard: {
    id: "standard",
    label: "Standard Installation Kit",
    suits: "45 × 90 mm F5 timber brackets and flat brackets",
    set: [
      { qty: 2, item: "40 mm outdoor timber screws", short: "40 mm screws" },
      { qty: 1, item: "M8 × 40 mm galvanised bolt", short: "M8 bolt" },
      { qty: 1, item: "M8 galvanised nut", short: "M8 nut" },
    ],
    confirmed: true,
  },
  plinth: {
    id: "plinth",
    label: "Plinth Installation Kit",
    suits: "25 × 150 mm plinth timber brackets",
    set: [
      { qty: 2, item: "20 mm outdoor screws", short: "20 mm screws" },
      { qty: 1, item: "M8 × 40 mm galvanised bolt", short: "M8 bolt" },
      { qty: 1, item: "M8 galvanised nut", short: "M8 nut" },
    ],
    confirmed: true,
  },
};

// Kit sizes match the bracket pack structure. Prices apply to both kit types.
// CONFIRMED PRICING (2026-07-12) — mirrored by the Shopify Installation Kit
// product variants; keep the two in sync.
export const KIT_PACKS = [
  { brackets: 1, price: 1.5 },
  { brackets: 10, price: 12 },
  { brackets: 20, price: 23 },
  { brackets: 30, price: 33 },
  { brackets: 40, price: 42 },
  { brackets: 50, price: 50 },
];

// What a kit of a given type/size contains: [{ qty, item, short }]
export const kitContents = (kitTypeId, brackets) => {
  const type = KIT_TYPES[kitTypeId];
  if (!type?.set) return [];
  return type.set.map((line) => ({ ...line, qty: line.qty * brackets }));
};

export const kitContentsLabel = (kitTypeId, brackets) =>
  kitContents(kitTypeId, brackets)
    .map((line) => `${line.qty} × ${line.short}`)
    .join(" · ");

// Variant naming — MUST match the Shopify Installation Kit variant option
// values exactly ("Standard — 20 bracket kit", "Plinth — 10 bracket kit").
export const kitVariantTitle = (kitTypeId, brackets) => {
  const prefix = kitTypeId === "plinth" ? "Plinth" : "Standard";
  return `${prefix} — ${brackets} bracket kit`;
};

// Customer-facing kit name for the add-on, e.g.
// "20-Bracket Standard Installation Kit".
export const kitDisplayName = (kitTypeId, brackets) => {
  const prefix = kitTypeId === "plinth" ? "Plinth" : "Standard";
  return `${brackets}-Bracket ${prefix} Installation Kit`;
};

// Matches a bracket quantity to installation-kit lines so the customer never
// has to work out the hardware count. Exact pack sizes map to one kit; custom
// quantities are decomposed greedily (e.g. 25 → 1 × 20-kit + 5 × 1-kit).
export function kitLinesForQuantity(qty) {
  const q = Math.max(1, Math.floor(Number(qty)) || 1);
  const sizes = [...KIT_PACKS].sort((a, b) => b.brackets - a.brackets);
  const lines = [];
  let remaining = q;
  for (const pack of sizes) {
    if (remaining <= 0) break;
    const count = Math.floor(remaining / pack.brackets);
    if (count > 0) {
      lines.push({ brackets: pack.brackets, count, price: pack.price });
      remaining -= count * pack.brackets;
    }
  }
  const total = lines.reduce((sum, line) => sum + line.price * line.count, 0);
  return { lines, total, covers: q };
}

// ----------------------------------------------------------------------------
// Dispatch wording — keep it human, not rigid. Confirm operationally before
// tightening any of these.
// ----------------------------------------------------------------------------
export const DISPATCH_NOTES = {
  slideOn: `Main stocked option. Usually dispatched within ${DISPATCH_WINDOW}.`,
  flatBracket: `Usually dispatched within ${DISPATCH_WINDOW}, the same as our supported bracket.`,
  nonStandard: "Selected sizes may require additional manufacturing time.",
};

// Kept for the flat-bracket FAQ/schema — batch manufacture window.
export const FLAT_BRACKET_LEAD_TIME = DISPATCH_NOTES.flatBracket;
